import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"

import {
  useGetIntegrationsHealth,
  useGetIntegrationsList
} from "@/api/useIntegrations"
import { Button, Flex, Tag, Text, Title } from "@/atoms"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { NoFound } from "@/icons"
import { ListIntegrationsSkeleton } from "@/pages/IntegrationsPage/components/organisms/ListIntegrationsSkeleton"
import { IntegrationCardTemplate } from "@/pages/IntegrationsPage/components/templates/IntegrationCardTemplate"
import { PARAMS } from "@/pages/IntegrationsPage/const"
import { HeaderCard } from "../../molecules/HeaderCard"

import styles from "./ListIntegrations.module.scss"

export const ListIntegrations = () => {
  const { response, isLoading } = useGetIntegrationsList()
  const { response: healthByName, isHealthLoading } = useGetIntegrationsHealth()
  const data = response?.data
  const [searchParams, setSearchParams] = useSearchParams()
  const { query, all, tag: param } = PARAMS
  const onClear = () => {
    searchParams.delete(query)
    setSearchParams(searchParams)
  }
  const { logger, EVENTS } = useLogger()
  const search = searchParams.get(query) || ""

  const currentTag = (
    search ? all : searchParams.get(param) || all
  ).toLocaleLowerCase()

  const list = useMemo(
    () =>
      (data || []).filter(({ displayName, tags }) => {
        if (search) {
          const value = search.toLocaleLowerCase()

          return (
            displayName.toLocaleLowerCase().includes(value) ||
            tags.some((tag) => tag.toLocaleLowerCase().includes(value))
          )
        }

        if (currentTag === all) return true

        const value = currentTag.toLocaleLowerCase()

        if (currentTag)
          return tags.some((tag) => tag.toLocaleLowerCase().includes(value))

        return true
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, search, currentTag]
  )

  const { t } = useTranslation("translation", {
    keyPrefix: "integrations.empty"
  })

  if (!isLoading && !list?.length)
    return (
      <Flex
        vertical
        flex={1}
        gap={15}
        align="center"
        className={styles.notFound}
      >
        <NoFound className={styles.notFoundIcon} />
        <Flex vertical>
          <Text type="secondary">{t("text")}</Text>
          {!!search && (
            <Button type="link" onClick={onClear}>
              {t("action")}
            </Button>
          )}
        </Flex>
      </Flex>
    )

  return (
    <div className={styles.list}>
      {isLoading && <ListIntegrationsSkeleton />}
      {!isLoading &&
        list.map(
          ({
            displayName,
            description,
            name,
            tags,
            isInstalled,
            isEnabled,
            isBeta,
            isNew,
            onlyLogo = false
          }) => (
            <IntegrationCardTemplate
              to={`/integrations/${name}`}
              key={name}
              isEnabled={isEnabled}
              header={
                <HeaderCard
                  name={name}
                  isInstalled={isInstalled}
                  isEnabled={isEnabled}
                  health={healthByName?.[name]}
                  isHealthLoading={isHealthLoading}
                  isBeta={isBeta}
                  isNew={isNew}
                  onlyLogo={onlyLogo}
                />
              }
              title={
                <Title className={styles.displayName} level={4}>
                  {displayName}
                </Title>
              }
              description={
                <Text className={styles.description} type="secondary">
                  {description}
                </Text>
              }
              tags={tags.map((tag: string) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
              onClick={() => {
                logger.info(EVENTS.ANALYTIC_EVENTS.INTEGRATIONS.CLICKED, {
                  name
                })
              }}
            />
          )
        )}
    </div>
  )
}
